/**
 * The lifecycle of a submitted order, as the ticket tells it.
 *
 * The order SDK's sheet already folds a placement into one `phase`
 * (`placing` → `filled | partial | resting | pending | failed` → `settled | cancelled`, following
 * the order by poll or forwarded stream frames). This module maps that onto greenbook's
 * three-beat strip — placing → resting → filled — and keeps the rules pure so they're testable
 * without a DOM (see orderFlow.test.ts).
 *
 * The ticket never closes itself (docs/ticket-ux.md §4): every result waits for the user.
 */
import type { PlaceResult, SheetPhase, TrackedOrder } from "@openmarketsai/order-sheet";

export type OrderPhase =
  /** Nothing submitted yet. */
  | "idle"
  /** Request in flight. */
  | "placing"
  /** On the book (or accepted, fill confirming off-call) — non-terminal, waits for the user. */
  | "resting"
  /** Every leg filled. */
  | "filled"
  /** At least one leg rejected/failed, or the request itself errored. */
  | "failed"
  /** A resting order the user cancelled. */
  | "cancelled";

function anyLegFailed(result: PlaceResult | null): boolean {
  return !!result?.legs.some((l) => l.status === "failed" || l.status === "rejected" || l.issue != null);
}

function anyLegWorking(result: PlaceResult | null): boolean {
  return !!result?.legs.some((l) => l.status === "resting" || l.status === "pending");
}

const UNFILLED_RESULTS = new Set(["cancelled", "canceled", "void", "refunded"]);
/** Every tracked order closed with nothing filled (and, if "settled", settled as a cancel/void). */
function neverFilled(orders: readonly TrackedOrder[]): boolean {
  return (
    orders.length > 0 &&
    orders.every((o) => (o.filledAmt || 0) <= 1e-9 && (!o.settled || UNFILLED_RESULTS.has(String(o.settled.result).toLowerCase())))
  );
}

/**
 * Collapse the sheet's phase (+ its last placement) into the ticket's phase.
 *
 * `partial` needs the result to read: the SDK reports `partial` both for a leg that was
 * declined after a partial fill (a failure the user must see) and for a market order whose
 * whole-contract rounding left a few cents unbought (a fill — greenbook always treated a
 * `completed` leg as filled). Any failure outranks everything; a still-working leg outranks
 * a fill, so a half-resting order never auto-closes.
 */
export function phaseFromSheet(
  phase: SheetPhase,
  result: PlaceResult | null,
  error: unknown = null,
  orders: readonly TrackedOrder[] = [],
): OrderPhase {
  switch (phase) {
    case "placing":
      return "placing";
    case "filled":
      return "filled";
    case "settled":
      // The SDK reads an order row's `result` as a settlement, so a venue-rejected order that
      // closed with nothing filled (`result: "cancelled"`) arrives here too. That's a decline,
      // not a fill.
      return neverFilled(orders) ? "failed" : "filled";
    case "resting":
    case "pending":
      return "resting";
    case "failed":
      return "failed";
    case "cancelled":
      return "cancelled";
    case "partial":
      if (anyLegFailed(result)) return "failed";
      if (anyLegWorking(result)) return "resting";
      return "filled";
    default:
      // idle / loading / ready / blocked / confirming: nothing on the wire. A refused request
      // (4xx) lands back on the slip as `blocked` with the error set.
      return error ? "failed" : "idle";
  }
}
